import { ApplicationQuoteType } from '@amsanalytics/react-shared-library';
import { IApplicationAssignableRole } from '../Interfaces/Common/IApplicationAssignableRole';
import { IApplicationAssignment } from '../Interfaces/Application/IApplicationAssignment';
import { Constants } from './constants';

const createdTime = (row: IApplicationAssignableRole): number => {
  const time = row.createdDate ? Date.parse(row.createdDate) : NaN;
  return Number.isNaN(time) ? 0 : time;
};

/**
 * The row that decides each assignable role on an application of `quoteType`, in sort order: the role's row for that
 * quote type, else its every-quote-type row (no quote type). The server's ApplicationAssignableRole.ForQuoteType, to the
 * letter: both pass the cases in wwda.unit.testing/TestData/AssignableRolesForQuoteType.json. A row allowing none
 * (NumberAllowed 0) is kept and gives the role no slots, which is how a quote type hides it. Two rows for the same role
 * and quote type resolve to the first by sort order, then the older.
 */
export function assignableRolesForQuoteType(
  rows: IApplicationAssignableRole[],
  quoteType: ApplicationQuoteType | null | undefined,
): IApplicationAssignableRole[] {
  const byRole = new Map<string, IApplicationAssignableRole>();
  const ranked = rows
    .filter((row) => !row.deletedDate)
    .filter((row) => row.quoteType == null || (quoteType != null && row.quoteType === quoteType))
    .sort((a, b) =>
      Number(a.quoteType == null) - Number(b.quoteType == null)
      || a.sortOrder - b.sortOrder
      || createdTime(a) - createdTime(b));
  for (const row of ranked) {
    if (!byRole.has(row.employeeRoleId)) byRole.set(row.employeeRoleId, row);
  }
  return [...byRole.values()].sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * The application's assignments after its quote type changes, with each slot the change takes away emptied: a slot that
 * the old quote type shows and the new one does not (its role hidden there, or fewer of it allowed). Emptied as the
 * card's "(unassigned)" choice empties one, so the next save ends the assignment rather than keeping one no slot shows.
 * Assignments outside the old quote type's slots are left as they are.
 */
export function clearSlotsHiddenByQuoteTypeChange(
  rows: IApplicationAssignableRole[],
  assignments: IApplicationAssignment[],
  oldQuoteType: ApplicationQuoteType | null | undefined,
  newQuoteType: ApplicationQuoteType | null | undefined,
): IApplicationAssignment[] {
  const allowed = (quoteType: ApplicationQuoteType | null | undefined) =>
    new Map(assignableRolesForQuoteType(rows, quoteType).map((row) => [row.employeeRoleId.toLowerCase(), row.numberAllowed]));
  const before = allowed(oldQuoteType);
  const after = allowed(newQuoteType);
  return assignments.map((assignment) => {
    const role = assignment.assignmentEmployeeRoleId.toLowerCase();
    const shownBefore = assignment.assignmentRoleSequence <= (before.get(role) ?? 0);
    const shownAfter = assignment.assignmentRoleSequence <= (after.get(role) ?? 0);
    return shownBefore && !shownAfter ? { ...assignment, assignedToId: Constants.emptyGuid } : assignment;
  });
}
